import { AssetPath, AssetPathEncodeState, type FileItem, type FileListResult, type FileUploadOptions } from '@gnsx/genesys.js';
import {
  deleteObject,
  getMetadata,
  listAll,
  ref,
  uploadBytes,
  uploadBytesResumable,
  uploadString,
} from 'firebase/storage';

import { firebaseConfig, storage } from '../../firebase/config';

import { AbstractProvider } from './abstract-provider';
import {
  StorageProviderType
} from './interfaces';

import type {
  FileChangeEvent,
  FileDownloadOptions,
  FileExistsOptions,
} from './interfaces';

/**
 * Firebase Storage provider implementation
 * Implements the IStorageProvider interface for Firebase Storage
 */
export class FirebaseStorageProvider extends AbstractProvider {
  private storage = storage();

  get providerType(): StorageProviderType {
    return StorageProviderType.FIREBASE;
  }

  requireProjectContext(): boolean {
    return true;
  }

  /**
   * Lists files and directories at the specified path
   */
  async doListFiles(path: AssetPath, recursive: boolean): Promise<FileListResult> {
    const storageRef = ref(this.storage, path.initialPath);

    try {
      // Firebase storage doesn't differentiate between files and directories
      // It uses a flat structure with paths
      const result = await listAll(storageRef);

      const filePromises = result.items.map(async (itemRef) => {
        try {
          const metadata = await getMetadata(itemRef);

          return {
            name: itemRef.name,
            path: itemRef.fullPath,
            absolutePath: itemRef.fullPath,
            size: metadata.size || 0,
            modifiedTime: new Date(metadata.updated || metadata.timeCreated),
            contentType: metadata.contentType,
            isDirectory: false
          } as FileItem;
        } catch (error) {
          console.error(`Error getting metadata for ${itemRef.fullPath}:`, error);
          return {
            name: itemRef.name,
            path: itemRef.fullPath,
            absolutePath: itemRef.fullPath,
            size: 0,
            modifiedTime: new Date(),
            isDirectory: false
          } as FileItem;
        }
      });

      // Prefixes are treated as directories in Firebase Storage
      const dirItems: FileItem[] = result.prefixes.map((prefixRef) => ({
        name: prefixRef.name,
        path: prefixRef.fullPath,
        absolutePath: prefixRef.fullPath,
        size: 0, // Directories don't have size in Firebase Storage
        modifiedTime: new Date(), // Directories don't have modified time in Firebase Storage
        isDirectory: true
      }));

      // Wait for all file metadata to be fetched
      const files = await Promise.all(filePromises);

      return {
        files,
        directories: dirItems
      };
    } catch (error) {
      console.error(`Error listing files at ${path}:`, error);
      throw new Error(`FirebaseStorageProvider: Failed to list files: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  /**
   * Uploads a file to the specified path
   */
  async uploadFile(
    path: AssetPath,
    content: Blob | File | string | ArrayBuffer,
    options?: FileUploadOptions
  ): Promise<{ path: string; name: string }> {
    const storageRef = ref(this.storage, path.initialPath);

    try {
      let uploadTask;
      const metadata = {
        contentType: options?.contentType,
        customMetadata: options?.metadata
      };

      if (options?.onProgress) {
        // If progress tracking is needed, use resumable upload
        if (typeof content === 'string') {
          uploadTask = uploadString(storageRef, content, undefined, metadata);
        } else {
          uploadTask = uploadBytesResumable(storageRef, content, metadata);

          // Set up progress tracking
          uploadTask.on('state_changed',
            (snapshot) => {
              const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
              options.onProgress?.(progress);
            },
            (error) => {
              console.error('Upload error:', error);
            }
          );
        }

        await uploadTask;
      } else {
        // Simple upload without progress tracking
        if (typeof content === 'string') {
          await uploadString(storageRef, content, undefined, metadata);
        } else {
          await uploadBytes(storageRef, content, metadata);
        }
      }

      return {
        path: storageRef.fullPath,
        name: storageRef.name
      };
    } catch (error) {
      console.error(`Error uploading file to ${path}:`, error);
      throw new Error(`Failed to upload file: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  private constructDownloadUrl(path: AssetPath): string {
    const encodedPath = encodeURIComponent(path.initialPath);
    return `https://firebasestorage.googleapis.com/v0/b/${firebaseConfig.storageBucket}/o/${encodedPath}?alt=media`;
  }

  /**
   * Downloads a file from the specified path
   */
  async downloadFile(
    path: AssetPath,
    options?: FileDownloadOptions
  ): Promise<Blob | string | ArrayBuffer> {
    try {
      // Get the download URL first
      const downloadUrl = this.constructDownloadUrl(path);

      // Use fetch to download the file with proper options
      console.time(`[Performance] downloadFile.fetch: ${path}`);
      const response = await fetch(downloadUrl, {
        method: 'GET',
        mode: 'cors',
        credentials: 'omit',
        headers: {
          'Accept': '*/*',
        }
      });
      console.timeEnd(`[Performance] downloadFile.fetch: ${path}`);

      if (!response.ok) {
        throw new Error(`Failed to download file: ${response.status} ${response.statusText}`);
      }

      // If explicitly requesting a blob, return it
      if (options?.asBlob) {
        return await response.blob();
      }

      const textContent = await this.extractTextContent(response, path.initialPath);
      if (textContent !== null) {
        return textContent;
      }

      // For all other cases, get as ArrayBuffer
      return await response.arrayBuffer();
    } catch (error) {
      // Log the full error for debugging
      console.error(`Error downloading file from ${path}:`, error);
      throw new Error(`Failed to download file: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  /**
   * Deletes a file or directory
   * Note: Firebase Storage doesn't have true directories, so for directories
   * we need to list all files within the path and delete them individually
   */
  async deleteItem(path: AssetPath, recursive = true): Promise<void> {
    try {
      const storageRef = ref(this.storage, path.initialPath);

      // Check if this is a "directory" (not a real concept in Firebase Storage)
      try {
        // Try to get metadata - if it succeeds, it's a file
        await getMetadata(storageRef);

        // If we get here, it's a file, so delete it
        await deleteObject(storageRef);
      } catch (error) {
        // If error, assume it's a "directory" (prefix)
        if (recursive) {
          // List all items at this path
          const result = await this.listFiles(path, true, true);

          // Delete all files
          const deletePromises = result.files.map(file =>
            this.deleteItem(AssetPath.fromString(file.path), false)
          );

          // Delete all subdirectories recursively
          const deleteDirPromises = result.directories.map(dir =>
            this.deleteItem(AssetPath.fromString(dir.path), true)
          );

          await Promise.all([...deletePromises, ...deleteDirPromises]);
        } else {
          throw new Error(`Cannot delete directory ${path} without recursive flag`);
        }
      }
    } catch (error) {
      console.error(`Error deleting item at ${path}:`, error);
      throw new Error(`Failed to delete item: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  /**
   * Creates a directory at the specified path
   * Note: Firebase Storage doesn't have actual directories,
   * so this method creates an empty placeholder file
   */
  async createDirectory(path: AssetPath): Promise<void> {
    try {
      // Firebase Storage doesn't have real directories
      // We create a placeholder file to simulate a directory
      const placeholderPath = AssetPath.join(path.initialPath, '.placeholder');
      await this.uploadFile(AssetPath.fromString(placeholderPath), new Blob([]));
    } catch (error) {
      console.error(`Error creating directory at ${path}:`, error);
      throw new Error(`Failed to create directory: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  /**
   * Checks if a file or directory exists
   */
  async exists(path: AssetPath, options?: FileExistsOptions): Promise<boolean> {
    try {
      const storageRef = ref(this.storage, path.initialPath);

      try {
        // Try to get metadata - if it succeeds, the file exists
        await getMetadata(storageRef);
        return true;
      } catch (error) {
        // Check if it might be a "directory" by listing contents
        const result = await listAll(storageRef);

        // If there are items or prefixes, it exists as a "directory"
        return result.items.length > 0 || result.prefixes.length > 0;
      }
    } catch (error) {
      return false;
    }
  }

  /**
   * Gets download URL for a file
   */
  async resolvePath(path: AssetPath, expiry?: number): Promise<AssetPath> {
    if (path.isResolved()) {
      return path;
    }
    try {
      // Firebase automatically creates URLs with expiry time
      const url = this.constructDownloadUrl(path);
      path.resolvePath(url, AssetPathEncodeState.Encoded);
      return path;
    } catch (error) {
      console.error(`Error getting URL for ${path}:`, error);
      throw new Error(`Failed to get file URL: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  /**
   * Handle file system changes
   * Note: Firebase Storage doesn't support real-time file system monitoring
   */
  onFileSystemChange(callback: (event: FileChangeEvent) => void): () => void {
    console.warn('File system change monitoring is not supported in Firebase Storage');
    return () => {}; // Return no-op unsubscribe function
  }
}
